import { useState, useEffect, useCallback } from "react";
import { Button } from "@telegram-apps/telegram-ui";
import { useGameStore, selectUser, selectCharacter, selectUpgradesInfo, selectSelectedLookId, selectLooks, selectIsInitialized } from "../../store/gameStore";
import { useI18n } from "../../utils/i18n/I18nProvider";
import Game from "../../scripts/game/Game";
import { Character } from "../../scripts/game/character/Character";
import { CharacteristicEnum } from "../../handlers/types";
import WeaponType from "../../scripts/enums/WeaponType";
import WEAPONS from "../../scripts/consts/WEAPONS";
import {
  getCharacteristicsByTier,
  getWeaponsByTier,
  getPowerupsByTier,
  getAllTiers,
} from "../../scripts/consts/CHARACTER_PROGRESSION";

import { getLookUpgradeDescription } from "../../utils/lookUtils";
import { formatNumber } from "../../utils/formatters";
import "./CharacterUpgrade.css";
import { useNavigate } from "react-router-dom";

interface CharacterUpgradeProps {
  characterId: string;
  onClose: () => void;
}

interface UpgradeCardProps {
  stat: CharacteristicEnum;
  currentLevel: number;
  maxLevel: number;
  currentValue: number;
  nextValue: number | null;
  upgradeCost: number | undefined;
  isLocked: boolean;
  userBalance: number;
  onUpgrade: (statKey: CharacteristicEnum) => void;
  isLoading: boolean;
  t: (key: string, options?: any) => string;
}

function UpgradeCard({
  stat,
  currentLevel,
  maxLevel,
  currentValue,
  nextValue,
  upgradeCost,
  isLocked,
  userBalance,
  onUpgrade,
  isLoading,
  t,
}: UpgradeCardProps) {
  const getStatDescription = (stat: string): string => {
    switch (stat) {
      case "health":
      case "maxHp":
        return t("characteristics.maxHp");
      case "damage":
      case "power":
        return t("characteristics.power");
      case "speed":
        return t("characteristics.speed");
      case "magnet":
        return t("characteristics.magnet");
      case "luck":
        return t("characteristics.luck");
      case "armor":
        return t("characteristics.armor");
      case "moveSpeed":
        return t("characteristics.moveSpeed");
      case "area":
        return t("characteristics.area");
      case "duration":
        return t("characteristics.duration");
      case "cooldown":
        return t("characteristics.cooldown");
      case "growth":
        return t("characteristics.growth");
      case "amount":
        return t("characteristics.amount");
      default:
        return "";
    }
  };

  const isMaxLevel = currentLevel >= maxLevel;
  const canAfford = upgradeCost !== undefined && userBalance >= upgradeCost;
  const canUpgrade = !isLocked && !isMaxLevel && canAfford && !isLoading;

  return (
    <div className={`upgrade-card ${isLocked ? "upgrade-card--locked" : ""}`}>
      <div className="upgrade-card__header">
        <h3
          className={`upgrade-card__title ${
            isLocked ? "upgrade-card__title--locked" : ""
          }`}
        >
          {t(`character.${stat}`)} {currentLevel}
        </h3>
      </div>

      <div className="upgrade-card__description">
        {getStatDescription(stat)}
      </div>

      {!isLocked && (
        <div className="upgrade-card__values">
          {nextValue !== null && !isMaxLevel ? (
            <div className="upgrade-card__next-level">
              {t("character.nextLevel", {
                current: formatNumber(currentValue),
                next: formatNumber(nextValue),
              })}
            </div>
          ) : (
            <div className="upgrade-card__current-level">
              {t("character.currentValue", {
                value: formatNumber(currentValue),
              })}
            </div>
          )}
        </div>
      )}

      {isLocked ? null : isMaxLevel ? (
        <div className="upgrade-card__maxed">{t("common.max")}</div>
      ) : (
        <Button
          size="s"
          mode={canAfford ? "filled" : "outline"}
          disabled={!canUpgrade}
          onClick={() => onUpgrade(stat)}
          className="upgrade-card__button"
        >
          {isLoading ? (
            t("common.loading")
          ) : upgradeCost !== undefined ? (
            <span className="upgrade-card__button-content">
              {t("common.upgrade")} {formatNumber(upgradeCost)}
              <img
                src="/assets/img/coin.webp"
                alt="Coin"
                className="upgrade-card__coin-icon"
              />
            </span>
          ) : (
            t("common.upgrade_btn")
          )}
        </Button>
      )}
    </div>
  );
}

interface WeaponCardProps {
  weaponType: WeaponType;
  weaponInfo: any;
  isPowerup: boolean;
  isLocked: boolean;
  t: (key: string, options?: any) => string;
}

function WeaponCard({
  weaponType,
  weaponInfo,
  isPowerup,
  isLocked,
  t,
}: WeaponCardProps) {
  const weaponName = t(`weapons.${weaponType}.name`.toLocaleLowerCase());
  const weaponDescription = t(
    `weapons.${weaponType}.description`.toLocaleLowerCase()
  );

  // Get the weapon icon from WEAPONS constant
  const weaponData = WEAPONS[weaponType]?.[0];
  const iconName = weaponData?.iconName;

  return (
    <div
      className={`weapon-card ${isLocked ? "weapon-card--locked" : ""}`}
      data-is-weapon={isLocked && !isPowerup ? "true" : undefined}
      data-is-powerup={isLocked && isPowerup ? "true" : undefined}
    >
      <div className="weapon-card__content">
        {iconName && !isLocked && (
          <img
            src={`/assets/img/${iconName}.webp`}
            alt={weaponName}
            className="weapon-card__icon"
            onError={(e) => {
              e.currentTarget.style.display = "none";
            }}
          />
        )}
        <div className="weapon-card__text">
          <h3
            className={`weapon-card__title ${
              isLocked ? "weapon-card__title--locked" : ""
            }`}
          >
            {isLocked
              ? isPowerup
                ? t("weapons.card.newPowerup")
                : t("weapons.card.newWeapon")
              : weaponName}
          </h3>
          <div
            className={`weapon-card__description ${
              isLocked ? "weapon-card__description--locked" : ""
            }`}
          >
            {isLocked
              ? t("weapons.card.somethingInteresting")
              : weaponDescription}
          </div>
        </div>
      </div>
    </div>
  );
}

interface TierSeparatorProps {
  tierLevel: number;
  isUnlocked: boolean;
  t: (key: string, options?: any) => string;
}

function TierSeparator({ tierLevel, isUnlocked, t }: TierSeparatorProps) {
  return (
    <div
      className={`tier-separator ${
        isUnlocked ? "tier-separator--unlocked" : "tier-separator--locked"
      }`}
    >
      <div className="tier-separator__line" />
      <div className="tier-separator__label">
        {t("characterUpgrade.upgradeToLevel", {
          level: tierLevel,
          defaultValue: `Upgrade to level ${tierLevel}`,
        })}
      </div>
      <div className="tier-separator__line" />
    </div>
  );
}

export function CharacterUpgrade({
  characterId,
  onClose,
}: CharacterUpgradeProps): JSX.Element {
  const { t } = useI18n();
  const navigate = useNavigate();

  // Use store selectors
  const user = useGameStore(selectUser);
  const character = useGameStore(selectCharacter);
  const upgradesInfo = useGameStore(selectUpgradesInfo);
  const selectedLookId = useGameStore(selectSelectedLookId);
  const userLooks = useGameStore(selectLooks);
  const isInitialized = useGameStore(selectIsInitialized);
  const upgradeCharacteristic = useGameStore.getState().upgradeCharacteristic;

  const userBalance = user?.balance ?? 0;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loadingButtons, setLoadingButtons] = useState<Set<string>>(new Set());
  const [showPartyEffect, setShowPartyEffect] = useState(false);

  useEffect(() => {
    if (isInitialized && !character) {
      setError(t("errors.failedToLoadCharacter"));
    } else if (isInitialized && character) {
      setError(null);
    }
  }, [isInitialized, character, t]);

  const handleUpgrade = async (statKey: CharacteristicEnum) => {
    if (!character || !upgradesInfo) return;

    const currentLevel = upgradesInfo.currentLevels?.[statKey] || 0;
    const upgradeInfo =
      upgradesInfo.upgrades[statKey as keyof typeof upgradesInfo.upgrades];
    if (!upgradeInfo || !upgradeInfo[currentLevel]) return;

    const baseCost = upgradeInfo[currentLevel].cost;
    if (!baseCost || userBalance < baseCost) return;

    try {
      // Add to loading buttons
      setLoadingButtons((prev) => new Set(prev).add(statKey));

      // Call the store action to upgrade the characteristic
      const success = await upgradeCharacteristic(statKey);

      if (success) {
        // Show party effect
        setShowPartyEffect(true);
        setTimeout(() => setShowPartyEffect(false), 3000);
      }
    } catch (error) {
      console.error("Error upgrading characteristic:", error);
    } finally {
      // Remove from loading buttons
      setLoadingButtons((prev) => {
        const newSet = new Set(prev);
        newSet.delete(statKey);
        return newSet;
      });
    }
  };

  const handleChangeLook = useCallback(() => {
    try {
      const characterId = Game.Core?.PlayerOptions?.SelectedCharacterId || "";
      const path = characterId
        ? `/character-looks/${characterId}`
        : "/character-looks";
      navigate(path);
    } catch (error) {
      console.error("Error navigating to CharacterLooks:", error);
    }
  }, [navigate]);

  if (loading) {
    return (
      <div className="character-upgrade">
        <div className="character-upgrade__loading">
          {t("common.loading")}...
        </div>
      </div>
    );
  }

  if (error || !character || !upgradesInfo) {
    return (
      <div className="character-upgrade">
        <div className="character-upgrade__error">
          {error || t("errors.failedToLoadCharacter")}
        </div>
      </div>
    );
  }

  const totalUpgrades = Object.values(upgradesInfo.currentLevels || {}).reduce(
    (sum, level) => sum + (level || 0),
    0
  );

  // Get the selected look for displaying the perk and sprite
  const selectedLook =
    userLooks.find((look) => look.id === selectedLookId) || null;

  // Get visuals from selected look if available, otherwise fallback to character's default
  const visuals = selectedLook
    ? {
        spriteName: selectedLook.spriteKey,
        animationKey: selectedLook.animationKey,
        scale: 1, // Default scale
      }
    : character.getCharacterVisuals();

  // Get all tiers and organized items
  const allTiers = getAllTiers();
  const characteristicsByTier = getCharacteristicsByTier();
  const weaponsByTier = getWeaponsByTier();
  const powerupsByTier = getPowerupsByTier();

  // Track processed items to avoid duplicates
  const processedStats = new Set<string>();
  const processedWeapons = new Set<WeaponType>();
  const processedPowerups = new Set<WeaponType>();

  const renderCards = () => {
    const cards: JSX.Element[] = [];

    // Process each tier in order
    for (const { level: tierLevel } of allTiers) {
      const tierCharacteristics = characteristicsByTier[tierLevel] || [];
      const tierWeapons = weaponsByTier[tierLevel] || [];
      const tierPowerups = powerupsByTier[tierLevel] || [];
      const isUnlocked = totalUpgrades >= tierLevel;

      // Add a tier separator if this isn't the first tier
      if (tierLevel > 0) {
        cards.push(
          <TierSeparator
            key={`tier-${tierLevel}`}
            tierLevel={tierLevel}
            isUnlocked={isUnlocked}
            t={t}
          />
        );
      }

      // Process weapons in this tier FIRST - but only if not tier 0
      if (tierLevel > 0) {
        for (const weaponType of tierWeapons) {
          if (processedWeapons.has(weaponType)) continue;

          processedWeapons.add(weaponType);
          const weaponInfo = WEAPONS[weaponType][0];

          cards.push(
            <WeaponCard
              key={`weapon-${weaponType}`}
              weaponType={weaponType}
              weaponInfo={weaponInfo}
              isPowerup={false}
              isLocked={!isUnlocked}
              t={t}
            />
          );
        }

        // Process powerups in this tier SECOND - but only if not tier 0
        for (const powerupType of tierPowerups) {
          if (processedPowerups.has(powerupType)) continue;

          processedPowerups.add(powerupType);
          const powerupInfo = WEAPONS[powerupType][0];

          cards.push(
            <WeaponCard
              key={`powerup-${powerupType}`}
              weaponType={powerupType}
              weaponInfo={powerupInfo}
              isPowerup={true}
              isLocked={!isUnlocked}
              t={t}
            />
          );
        }
      }

      // Process characteristics in this tier LAST
      for (const statKey of tierCharacteristics) {
        if (
          processedStats.has(statKey) ||
          !upgradesInfo.upgrades[statKey] ||
          upgradesInfo.upgrades[statKey].length === 0
        ) {
          continue;
        }

        processedStats.add(statKey);

        const upgradeInfo = upgradesInfo.upgrades[statKey];
        const currentLevel = upgradesInfo.currentLevels?.[statKey] || 0;
        const maxLevel = upgradeInfo.length;
        const currentValue = upgradesInfo.currentStats?.[statKey] ?? 0;
        const nextValue =
          currentLevel < maxLevel
            ? currentValue + upgradeInfo[currentLevel].increment
            : null;

        cards.push(
          <UpgradeCard
            key={`stat-${statKey}`}
            stat={statKey}
            currentLevel={currentLevel}
            maxLevel={maxLevel}
            currentValue={currentValue}
            nextValue={nextValue}
            upgradeCost={upgradeInfo[currentLevel]?.cost}
            isLocked={!isUnlocked}
            userBalance={userBalance}
            onUpgrade={handleUpgrade}
            isLoading={loadingButtons.has(statKey)}
            t={t}
          />
        );
      }
    }

    return cards;
  };

  return (
    <div className="character-upgrade">
      {/* Party Effect */}
      {showPartyEffect && (
        <div className="character-upgrade__party-effect">
          {Array.from({ length: 20 }, (_, i) => (
            <div
              key={i}
              className="character-upgrade__confetti"
              style={{
                left: `${Math.random() * 100}%`,
                animationDuration: `${4 + Math.random() * 2}s`,
              }}
            />
          ))}
          {Array.from({ length: 10 }, (_, i) => (
            <div
              key={i}
              className="character-upgrade__emoji"
              style={{
                left: `${Math.random() * 100}%`,
                animationDuration: `${3 + Math.random() * 1.5}s`,
              }}
            >
              🎉
            </div>
          ))}
        </div>
      )}

      {/* Header */}
      <div className="character-upgrade__header">
        <h1 className="character-upgrade__title">
          {t("character.upgradeCharacter")}
        </h1>
        <p className="character-upgrade__subtitle">
          {t("character.advancedLevelsRequireAdvancedCapabilities")}
        </p>

        <div className="character-upgrade__character">
          <div className="character-upgrade__character-placeholder">
            <div className="character-upgrade__character-sprite">
              <div
                className="character-upgrade__character-sprite-frame"
                style={{
                  background: `url(/assets/img/characters/${visuals.spriteName}.webp) -60px -8px`,
                }}
                aria-label={t("character.character")}
                role="img"
              />
            </div>
          </div>
        </div>

        <div className="character-upgrade__character-info">
          <div className="character-upgrade__look-perk">
            {t("character.lookPerk")}{" "}
            <span className="character-upgrade__look-perk-value">
              {getLookUpgradeDescription(selectedLook, t)}
            </span>
          </div>
          <div className="character-upgrade__upgrade-level">
            {t("character.upgradeLevelInfo", { level: totalUpgrades })}
          </div>
          <div className="character-upgrade__coins">
            {t("character.youHaveCoins", { amount: userBalance })}
            <img
              src="/assets/img/coin.webp"
              alt="Coin"
              className="character-upgrade__coins-icon"
            />
          </div>
        </div>

        <div className="character-upgrade__buttons">
          <Button
            className="character-upgrade__button character-upgrade__button--primary"
            size="s"
            onClick={handleChangeLook}
          >
            {t("character.changeLook").toUpperCase()}
          </Button>
          {/* <Button
            className="character-upgrade__button character-upgrade__button--secondary"
            size="s"
            disabled
            onClick={() => console.log("Sell Hero clicked")}
          >
            {t("character.sellHero").toUpperCase()}
          </Button> */}
        </div>
      </div>

      {/* Cards Container */}
      <div className="character-upgrade__cards-container">
        <div className="character-upgrade__cards">{renderCards()}</div>
      </div>
    </div>
  );
}
